import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/Timeline/TimelineRenderer.tsx
import { ChatStreamStatus } from "../../../domain/index.mjs";
import { ClarifyingQuestionsTimelineItem } from "./ClarifyingQuestions/ClarifyingQuestionsTimelineItem.mjs";
import { ContentRenderer } from "./ContentRenderer/index.mjs";
import { InlineFormTimelineItem } from "./InlineForm/InlineFormTimelineItem.mjs";
import { OrderStatusTimelineItem } from "./OrderStatusCard/OrderStatusTimelineItem.mjs";
import { ProductCardTimelineItem } from "./ProductCard/ProductCardTimelineItem.mjs";
import { ProductListTimelineItem } from "./ProductList/ProductListTimelineItem.mjs";
import { ThinkingIndicator } from "./ThinkingIndicator/index.mjs";
import { ToolStatusIndicator } from "./ToolStatusIndicator/index.mjs";
import { Fragment, jsx } from "react/jsx-runtime";
function itemKey(item, index) {
  switch (item.type) {
    case "pending_action":
      return `pending_action:${item.approvalID}`;
    case "applied_change":
      return `applied_change:${item.changeID}`;
    case "tool_card":
      return item.toolCallID === void 0 ? `tool_card-${index}` : `tool_card:${item.toolCallID}`;
    default:
      return `${item.type}-${index}`;
  }
}
function TimelineRenderer({ timeline, sessionStatus, productMap, storeDomain, onAddToCart, onClarifyingQuestionsComplete, onInlineFormSubmit, onInlineFormDismiss, dashboardMode = false, clarifyingSuperseded = false, inlineFormSuperseded = false, onToolClick, renderers }) {
  const filteredTimeline = timeline.filter((item, index) => {
    const isLastItem = index === timeline.length - 1;
    if (item.type === "possible_replies") {
      return false;
    }
    if (item.type === "thinking" && !item.content && !isLastItem) {
      return false;
    }
    return true;
  });
  return /* @__PURE__ */ jsx(Fragment, { children: filteredTimeline.map((item, index) => /* @__PURE__ */ jsx(
    TimelineItem,
    {
      item,
      isLastItem: index === filteredTimeline.length - 1,
      sessionStatus,
      productMap,
      storeDomain,
      onAddToCart,
      onClarifyingQuestionsComplete,
      onInlineFormSubmit,
      onInlineFormDismiss,
      dashboardMode,
      clarifyingSuperseded,
      inlineFormSuperseded,
      onToolClick,
      renderers
    },
    itemKey(item, index)
  )) });
}
function TimelineItem({ item, isLastItem, sessionStatus, productMap, storeDomain, onAddToCart, onClarifyingQuestionsComplete, onInlineFormSubmit, onInlineFormDismiss, dashboardMode, clarifyingSuperseded = false, inlineFormSuperseded = false, onToolClick, renderers }) {
  switch (item.type) {
    case "thinking":
      return /* @__PURE__ */ jsx(ThinkingIndicator, { content: item.content, isLastItem, sessionStatus, resolveLink: renderers?.resolveLink });
    case "content":
      return /* @__PURE__ */ jsx(
        ContentRenderer,
        {
          content: item.content,
          isLastItem,
          sessionStatus,
          productMap,
          storeDomain,
          resolveLink: renderers?.resolveLink
        }
      );
    case "tool":
      return /* @__PURE__ */ jsx(
        ToolStatusIndicator,
        {
          toolName: item.toolName,
          toolCallId: item.toolCallId,
          status: item.status,
          failedReason: item.failedReason,
          onToolClick,
          sessionStatus
        }
      );
    case "product_card": {
      return /* @__PURE__ */ jsx(ProductCardTimelineItem, { product: item.product, loading: item.loading, storeDomain, onAddToCart });
    }
    case "product_list": {
      return /* @__PURE__ */ jsx(ProductListTimelineItem, { products: item.products, loading: item.loading, storeDomain, onAddToCart });
    }
    case "order_status": {
      return /* @__PURE__ */ jsx(OrderStatusTimelineItem, { orders: item.orders, loading: item.loading });
    }
    case "clarifying_questions": {
      const cqItem = item;
      const superseded = clarifyingSuperseded && (!cqItem.answers || cqItem.answers.length === 0);
      const effectiveAnswers = superseded ? item.questions.map((_, i) => ({ questionIndex: i, answer: "", skipped: true })) : cqItem.answers;
      const mergedQuestions = effectiveAnswers?.length ? item.questions.map((q, i) => {
        const a = effectiveAnswers[i];
        return a ? { ...q, ...a.skipped ? { isSkipped: true } : { answer: a.answer } } : q;
      }) : item.questions;
      return /* @__PURE__ */ jsx(
        ClarifyingQuestionsTimelineItem,
        {
          questions: mergedQuestions,
          total: "total" in item ? item.total : void 0,
          turnFailed: sessionStatus === ChatStreamStatus.Failure,
          onComplete: onClarifyingQuestionsComplete,
          dashboardMode
        }
      );
    }
    case "inline_form": {
      const hasSubmittedValues = item.values != null && Object.keys(item.values).length > 0;
      const mergedFields = hasSubmittedValues ? item.fields.map((f) => ({ ...f, value: item.values?.[f.name] ?? "" })) : item.fields;
      const superseded = inlineFormSuperseded && !hasSubmittedValues && !item.isDismissed;
      return /* @__PURE__ */ jsx(
        InlineFormTimelineItem,
        {
          fields: mergedFields,
          title: item.title,
          submitLabel: item.submitLabel,
          isDismissed: item.isDismissed,
          onSubmit: onInlineFormSubmit,
          onDismiss: onInlineFormDismiss,
          dashboardMode,
          readOnly: superseded
        }
      );
    }
    case "pending_action":
      return renderers?.renderPendingAction?.(item) ?? null;
    case "applied_change":
      return renderers?.renderAppliedChange?.(item) ?? null;
    case "tool_card":
      return renderers?.renderToolCard?.(item) ?? null;
    default:
      return null;
  }
}
export {
  TimelineRenderer
};
//# sourceMappingURL=TimelineRenderer.mjs.map